/**
 * TelemetryDataFactory - Utility for creating telemetry data objects
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for creating and managing telemetry data
 * for tracking operation timing and performance metrics.
 */

import type { LayoutTelemetry, LayoutId } from '../../../../wordprocessing/core/types/ReactiveMessages';

/**
 * Telemetry timing data
 */
export interface TelemetryTiming {
  startMs: number;
  endMs: number;
  durationMs: number;
}

/**
 * Create initial telemetry data for an operation
 * 
 * Creates a telemetry data object with timing initialized to start time.
 * 
 * @param layoutId - Layout/request identifier (must be number for LayoutTelemetry)
 * @param operation - Operation name (must be one of: 'init', 'applyEdit', 'requestPagination', 'updatePageSetup')
 * @returns Partial telemetry data object with timing initialized
 */
export function createTelemetryData(
  layoutId: LayoutId,
  operation: 'init' | 'applyEdit' | 'requestPagination' | 'updatePageSetup'
): Partial<LayoutTelemetry> {
  return {
    layoutId,
    operation,
    timing: {
      startMs: performance.now(),
      endMs: 0,
      durationMs: 0
    }
  };
}

/**
 * Update telemetry timing with end time
 * 
 * Calculates duration based on start and end times.
 * 
 * @param timing - Timing object to update
 * @param endMs - End time in milliseconds (defaults to current time)
 * @returns Updated timing object
 */
export function updateTelemetryTiming(
  timing: TelemetryTiming,
  endMs?: number
): TelemetryTiming {
  const end = endMs ?? performance.now();
  return {
    ...timing,
    endMs: end,
    durationMs: end - timing.startMs
  };
}

/**
 * Create telemetry timing from start time
 * 
 * @param startMs - Start time in milliseconds (defaults to current time)
 * @returns Timing object initialized with start time
 */
export function createTelemetryTiming(startMs?: number): TelemetryTiming {
  const start = startMs ?? performance.now();
  return {
    startMs: start,
    endMs: 0,
    durationMs: 0
  };
}

/**
 * Calculate duration from start and end times
 * 
 * @param startMs - Start time in milliseconds
 * @param endMs - End time in milliseconds (defaults to current time)
 * @returns Duration in milliseconds
 */
export function calculateDuration(startMs: number, endMs?: number): number {
  const end = endMs ?? performance.now();
  return end - startMs;
}

